import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getAccessToken } from '../utils/auth';
import { getPredictionQualityHistory } from '../utils/httpApi';

/**
 * Modal + SVG line chart of daily prediction-quality metrics.
 * For EIDW we run THREE models in parallel — v11 (point-estimate baseline),
 * v12-quantile (XGBoost quantile regression), and v12-conformal (quantile +
 * split-conformal calibration). The conformal layer only widens/narrows the
 * interval AROUND the median; it does not change the median itself, so its
 * point-estimate metrics are identical to v12-quantile by construction.
 *
 * To avoid a redundant overlay (and to make the v12-quantile line actually
 * visible), this chart shows v11 + v12-quantile only. v12-conformal is still
 * fetched for the headline-summary tile so users see it exists; its
 * accuracy / interval-coverage metrics will get a separate dedicated view.
 *
 * For other airports (LPPR) only v2 is live, so a single line is drawn.
 */
function PredictionQualityChart({ airportIcao, onClose }) {
  // Models we always fetch (so headline tiles show every variant).
  const fetchModels = useMemo(
    () => airportIcao === 'EIDW' ? ['v11', 'v12-quantile', 'v12-conformal'] : ['v2'],
    [airportIcao]
  );

  // Models we draw on the chart. v12-conformal omitted — its scalar is
  // identical to v12-quantile by definition, so plotting it puts a line
  // exactly on top of v12-quantile (hides it AND blocks the tooltip).
  const chartModels = useMemo(
    () => airportIcao === 'EIDW' ? ['v11', 'v12-quantile'] : ['v2'],
    [airportIcao]
  );

  const MODEL_CONFIG = {
    'v11':           { label: 'v11 (point)',     color: '#22c55e' },
    'v12-quantile':  { label: 'v12-quantile',    color: '#60a5fa' },
    'v12-conformal': { label: 'v12-conformal',   color: '#f97316' },
    'v2':            { label: 'v2 (legacy)',     color: '#22c55e' },
  };

  const [seriesByModel, setSeriesByModel] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(14);
  const [granularity, setGranularity] = useState('day');
  const [hoverBucket, setHoverBucket] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getAccessToken();
        const responses = await Promise.all(
          fetchModels.map(mv =>
            getPredictionQualityHistory(airportIcao, mv, days, token, granularity)
              .then(r => ({ mv, r }))
              .catch(e => ({ mv, error: e }))
          )
        );
        if (cancelled) return;
        const next = {};
        let firstErr = null;
        for (const { mv, r, error: e } of responses) {
          if (e) { firstErr = firstErr || e.message || 'Network error'; continue; }
          if (r.header?.apiStatus === 'OK' || r.header?.apiStatus === 'SUCCESS') {
            next[mv] = r.days || [];
          } else {
            firstErr = firstErr || (r.header?.errors?.[0]?.message || 'Failed to load');
          }
        }
        setSeriesByModel(next);
        if (firstErr && Object.keys(next).length === 0) setError(firstErr);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [airportIcao, fetchModels, days, granularity]);

  const bucketKeys = useMemo(() => {
    const set = new Set();
    for (const series of Object.values(seriesByModel)) {
      for (const d of series) {
        const k = d.hour != null ? `${d.date}|${String(d.hour).padStart(2, '0')}` : d.date;
        set.add(k);
      }
    }
    return Array.from(set).sort();
  }, [seriesByModel]);

  const lookups = useMemo(() => {
    const out = {};
    for (const [mv, series] of Object.entries(seriesByModel)) {
      const m = new Map();
      for (const d of series) {
        const k = d.hour != null ? `${d.date}|${String(d.hour).padStart(2, '0')}` : d.date;
        m.set(k, d);
      }
      out[mv] = m;
    }
    return out;
  }, [seriesByModel]);

  // SVG chart dims
  const W = 720, H = 280, PAD_L = 48, PAD_R = 16, PAD_T = 24, PAD_B = 40;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const { xFor, yFor, yMax, xTicks, yTicks } = useMemo(() => {
    if (bucketKeys.length === 0) {
      return { xFor: () => 0, yFor: () => 0, yMax: 10, xTicks: [], yTicks: [] };
    }
    const ys = [];
    for (const mv of chartModels) {
      const series = seriesByModel[mv] || [];
      for (const d of series) if (d.medAE_60 != null) ys.push(d.medAE_60);
    }
    const yMaxRaw = ys.length > 0 ? Math.max(...ys) : 10;
    const yMax = Math.max(5, Math.ceil(yMaxRaw * 1.15));
    const xFor = i => PAD_L + (bucketKeys.length <= 1 ? chartW / 2 : (i / (bucketKeys.length - 1)) * chartW);
    const yFor = v => PAD_T + chartH - (v / yMax) * chartH;
    const stride = Math.max(1, Math.ceil(bucketKeys.length / 8));
    const xTicks = bucketKeys
      .map((k, i) => {
        const [date, hr] = k.split('|');
        return { i, label: hr ? `${date.slice(5)} ${hr}h` : date.slice(5) };
      })
      .filter(t => t.i % stride === 0);
    const yTickCount = 5;
    const yTicks = Array.from({ length: yTickCount + 1 }, (_, k) => (yMax * k) / yTickCount);
    return { xFor, yFor, yMax, xTicks, yTicks };
  }, [bucketKeys, seriesByModel, chartModels, chartW, chartH]);

  const linePath = (mv) => {
    const map = lookups[mv];
    if (!map) return '';
    const pts = bucketKeys
      .map((k, i) => {
        const d = map.get(k);
        if (!d || d.medAE_60 == null) return null;
        return `${xFor(i)},${yFor(d.medAE_60)}`;
      })
      .filter(Boolean);
    return pts.length ? 'M' + pts.join(' L') : '';
  };

  const latestByModel = useMemo(() => {
    const out = {};
    for (const [mv, series] of Object.entries(seriesByModel)) {
      out[mv] = series.length > 0 ? series[series.length - 1] : null;
    }
    return out;
  }, [seriesByModel]);

  const noData = !loading && !error && bucketKeys.length === 0;
  const headerLabel = airportIcao === 'EIDW' ? `${airportIcao} — model comparison` : airportIcao;

  // Mouse handler — snap to nearest bucket on the X axis.
  const onSvgMouseMove = (e) => {
    if (!svgRef.current || bucketKeys.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    // Map clientX → SVG x in unscaled units.
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    if (svgX < PAD_L || svgX > W - PAD_R) { setHoverBucket(null); return; }
    const frac = (svgX - PAD_L) / chartW;
    const idx = Math.round(frac * (bucketKeys.length - 1));
    const clamped = Math.max(0, Math.min(bucketKeys.length - 1, idx));
    setHoverBucket(clamped);
  };
  const onSvgMouseLeave = () => setHoverBucket(null);

  const hoverData = useMemo(() => {
    if (hoverBucket == null) return null;
    const k = bucketKeys[hoverBucket];
    if (!k) return null;
    const out = { key: k, x: xFor(hoverBucket), entries: [] };
    for (const mv of fetchModels) {
      const cfg = MODEL_CONFIG[mv] || { label: mv, color: '#9ca3af' };
      const d = lookups[mv]?.get(k);
      if (d) out.entries.push({ mv, cfg, d });
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverBucket, bucketKeys, fetchModels, lookups, xFor]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1f2937', color: '#e5e7eb', padding: 20, borderRadius: 8,
          maxWidth: 940, width: '95vw', maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>Prediction Quality — {headerLabel}</h3>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
              Median absolute error (minutes) at 10–60 NM. Per-flight median, then mean across flights. Lower is better.
            </div>
          </div>
          <div>
            <select value={granularity} onChange={e => {
                const g = e.target.value;
                setGranularity(g);
                if (g === 'hour' && days > 7) setDays(3);
              }}
              style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #374151', padding: '4px 8px', borderRadius: 4, marginRight: 8 }}>
              <option value="day">Daily</option>
              <option value="hour">Hourly</option>
            </select>
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #374151', padding: '4px 8px', borderRadius: 4, marginRight: 8 }}>
              {granularity === 'hour' ? (
                <>
                  <option value={1}>Today (hourly)</option>
                  <option value={2}>Last 2 days (hourly)</option>
                  <option value={3}>Last 3 days (hourly)</option>
                  <option value={7}>Last 7 days (hourly)</option>
                </>
              ) : (
                <>
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                </>
              )}
            </select>
            <button onClick={onClose} style={{ background: '#374151', color: '#e5e7eb', border: 'none', padding: '4px 12px', borderRadius: 4, cursor: 'pointer' }}>Close</button>
          </div>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>}
        {error && <div style={{ padding: 20, color: '#ef4444' }}>Error: {error}</div>}
        {noData && (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            No quality data for the selected window. Landed flights with predictions are required to compute metrics.
          </div>
        )}

        {!loading && !error && !noData && (
          <>
            {/* Headline summary tiles — one row per model (all 3 for EIDW so
                the conformal layer is acknowledged even if it doesn't draw). */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 16 }}>
              {fetchModels.map(mv => {
                const cfg = MODEL_CONFIG[mv] || { label: mv, color: '#9ca3af' };
                const latest = latestByModel[mv];
                if (!latest) {
                  return (
                    <div key={mv} style={{
                      display: 'grid', gridTemplateColumns: '160px repeat(4, 1fr)',
                      gap: 12, alignItems: 'center', background: '#111827',
                      border: `1px solid ${cfg.color}`, padding: 8, borderRadius: 6
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 5, background: cfg.color, display: 'inline-block' }} />
                        <span style={{ fontWeight: 600 }}>{cfg.label}</span>
                      </div>
                      <div style={{ color: '#6b7280', gridColumn: 'span 4' }}>No data</div>
                    </div>
                  );
                }
                return (
                  <div key={mv} style={{
                    display: 'grid', gridTemplateColumns: '160px repeat(4, 1fr)',
                    gap: 12, alignItems: 'center', background: '#111827',
                    border: `1px solid ${cfg.color}`, padding: 8, borderRadius: 6
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 5, background: cfg.color, display: 'inline-block' }} />
                      <span style={{ fontWeight: 600 }}>{cfg.label}</span>
                    </div>
                    <Stat label={`MedAE 10-60 NM (${latest.date})`} value={latest.medAE_60} unit="m" highlightColor={cfg.color} />
                    <Stat label="Bias" value={latest.bias_60} unit="m" signed />
                    <Stat label="Skill vs naive" value={latest.skillScore} fmt={v => v.toFixed(2)} subtle={latest.skillScore != null && latest.skillScore >= 1} />
                    <Stat label="Flights" value={latest.flightsScored} fmt={v => v.toFixed(0)} />
                  </div>
                );
              })}
            </div>

            {/* SVG overlay chart with custom HTML tooltip (single tooltip
                showing every model's value at the hovered bucket). */}
            <div style={{ position: 'relative' }}>
              <svg
                ref={svgRef}
                width={W}
                height={H}
                style={{ maxWidth: '100%', height: 'auto', background: '#111827', borderRadius: 6, display: 'block' }}
                onMouseMove={onSvgMouseMove}
                onMouseLeave={onSvgMouseLeave}
              >
                {yTicks.map((t, k) => (
                  <g key={k}>
                    <line x1={PAD_L} x2={W - PAD_R} y1={yFor(t)} y2={yFor(t)}
                      stroke="#374151" strokeWidth={1} strokeDasharray="2 3" />
                    <text x={PAD_L - 6} y={yFor(t) + 4} fill="#9ca3af" fontSize={11} textAnchor="end">{t.toFixed(1)}m</text>
                  </g>
                ))}
                {xTicks.map((t, k) => (
                  <text key={k} x={xFor(t.i)} y={H - PAD_B + 16} fill="#9ca3af" fontSize={11} textAnchor="middle">{t.label}</text>
                ))}
                {/* Hover crosshair */}
                {hoverData && (
                  <line
                    x1={hoverData.x} x2={hoverData.x}
                    y1={PAD_T} y2={PAD_T + chartH}
                    stroke="#9ca3af" strokeDasharray="3 3" strokeWidth={1} pointerEvents="none"
                  />
                )}
                {chartModels.map(mv => {
                  const cfg = MODEL_CONFIG[mv] || { color: '#9ca3af' };
                  return (
                    <path key={mv} d={linePath(mv)} fill="none" stroke={cfg.color} strokeWidth={2.2} pointerEvents="none" />
                  );
                })}
                {/* Plot only chartModels' dots; v12-conformal isn't a separate
                    series here (identical scalar to v12-quantile). */}
                {chartModels.map(mv => {
                  const cfg = MODEL_CONFIG[mv] || { color: '#9ca3af' };
                  const map = lookups[mv];
                  if (!map) return null;
                  return bucketKeys.map((k, i) => {
                    const d = map.get(k);
                    if (!d || d.medAE_60 == null) return null;
                    return (
                      <circle key={`${mv}-${i}`} cx={xFor(i)} cy={yFor(d.medAE_60)} r={3} fill={cfg.color} pointerEvents="none" />
                    );
                  });
                })}
                {/* Legend */}
                <g transform={`translate(${PAD_L + 8}, ${PAD_T + 8})`} pointerEvents="none">
                  {chartModels.map((mv, idx) => {
                    const cfg = MODEL_CONFIG[mv] || { label: mv, color: '#9ca3af' };
                    return <LegendDot key={mv} color={cfg.color} label={cfg.label} x={idx * 130} />;
                  })}
                </g>
              </svg>
              {hoverData && hoverData.entries.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${(hoverData.x / W) * 100}%`,
                    top: 8,
                    transform: hoverData.x > W * 0.6 ? 'translateX(-100%)' : 'translateX(8px)',
                    background: '#0b1020',
                    border: '1px solid #374151',
                    borderRadius: 4,
                    padding: '6px 10px',
                    fontSize: 11,
                    color: '#e5e7eb',
                    minWidth: 200,
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ color: '#9ca3af', marginBottom: 4 }}>
                    {hoverData.key.replace('|', ' ')}h
                  </div>
                  {hoverData.entries.map(({ mv, cfg, d }) => (
                    <div key={mv} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: cfg.color }} />
                      <span style={{ minWidth: 110 }}>{cfg.label}</span>
                      <span>MedAE {d.medAE_60 != null ? d.medAE_60.toFixed(2) + 'm' : '-'}</span>
                      <span style={{ marginLeft: 'auto', color: d.bias_60 != null && Math.abs(d.bias_60) > 1 ? '#ef4444' : '#9ca3af' }}>
                        bias {d.bias_60 != null ? (d.bias_60 > 0 ? '+' : '') + d.bias_60.toFixed(2) + 'm' : '-'}
                      </span>
                    </div>
                  ))}
                  <div style={{ color: '#6b7280', marginTop: 4, fontSize: 10 }}>
                    flights {hoverData.entries[0]?.d?.flightsScored ?? '-'} · skill {hoverData.entries[0]?.d?.skillScore != null ? hoverData.entries[0].d.skillScore.toFixed(2) : '-'}
                  </div>
                </div>
              )}
            </div>

            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, lineHeight: 1.5 }}>
              Skill &lt; 1.0 means the model beats the naive distance/350kts estimate. Bias near zero indicates no systematic over/under-prediction.
              Hover the chart for hour/day details. <strong>v12-conformal</strong> is omitted from the line plot — its point estimate is identical to v12-quantile (the conformal layer only modifies interval widths around the median, not the median itself).
            </div>

            <details style={{ marginTop: 14 }}>
              <summary style={{ cursor: 'pointer', color: '#9ca3af', fontSize: 12 }}>Daily values — all models</summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <th style={{ textAlign: 'left', padding: 4 }}>Bucket</th>
                    <th style={{ textAlign: 'left', padding: 4 }}>Model</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>MedAE 10-60</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>10-30</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>30-60</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Bias</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Skill</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Flights</th>
                    <th style={{ textAlign: 'right', padding: 4 }}>Preds</th>
                  </tr>
                </thead>
                <tbody>
                  {[...bucketKeys].reverse().flatMap(k => fetchModels.map(mv => {
                    const cfg = MODEL_CONFIG[mv] || { label: mv, color: '#9ca3af' };
                    const d = lookups[mv]?.get(k);
                    if (!d) return null;
                    return (
                      <tr key={`${k}-${mv}`} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: 4 }}>{d.date}{d.hour != null ? ` ${d.hour}h` : ''}</td>
                        <td style={{ padding: 4, color: cfg.color }}>{cfg.label}</td>
                        <td style={{ textAlign: 'right', padding: 4, color: cfg.color }}>{fmt(d.medAE_60)}</td>
                        <td style={{ textAlign: 'right', padding: 4 }}>{fmt(d.medAE_10_30)}</td>
                        <td style={{ textAlign: 'right', padding: 4 }}>{fmt(d.medAE_30_60)}</td>
                        <td style={{ textAlign: 'right', padding: 4, color: d.bias_60 != null && Math.abs(d.bias_60) > 1 ? '#ef4444' : '#e5e7eb' }}>{fmt(d.bias_60, true)}</td>
                        <td style={{ textAlign: 'right', padding: 4 }}>{d.skillScore != null ? d.skillScore.toFixed(2) : '-'}</td>
                        <td style={{ textAlign: 'right', padding: 4 }}>{d.flightsScored}</td>
                        <td style={{ textAlign: 'right', padding: 4 }}>{d.predictionsInBand}</td>
                      </tr>
                    );
                  }).filter(Boolean))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

function fmt(v, signed) {
  if (v == null) return '-';
  const s = v.toFixed(2);
  return signed && v > 0 ? `+${s}m` : `${s}m`;
}

function Stat({ label, value, unit, fmt: fmtFn, signed, subtle, highlightColor }) {
  const display = value == null ? '-'
    : fmtFn ? fmtFn(value)
    : `${signed && value > 0 ? '+' : ''}${value.toFixed(2)}${unit || ''}`;
  return (
    <div>
      <div style={{ fontSize: 10, color: '#9ca3af' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: subtle ? '#f97316' : (highlightColor || '#e5e7eb'), marginTop: 1 }}>{display}</div>
    </div>
  );
}

function LegendDot({ color, label, x }) {
  return (
    <g transform={`translate(${x}, 0)`}>
      <circle cx={6} cy={6} r={4} fill={color} />
      <text x={14} y={10} fill="#9ca3af" fontSize={11}>{label}</text>
    </g>
  );
}

export default PredictionQualityChart;
